/**
 * Cache Performance Monitoring Utilities
 * Tracks cache hit rates, performance metrics, and optimization opportunities
 */

export interface CacheMetrics {
  hitRate: number;
  missRate: number;
  totalRequests: number;
  averageResponseTime: number;
  cacheSize: number;
  lastCleanup: number;
}

export interface PerformanceEntry {
  url: string;
  method: string;
  responseTime: number;
  cacheHit: boolean;
  timestamp: number;
  size?: number;
}

class CachePerformanceMonitor {
  private static instance: CachePerformanceMonitor;
  private entries: PerformanceEntry[] = [];
  private maxEntries = 1000; // Keep last 1000 entries
  private metricsCache: CacheMetrics | null = null;
  private metricsCacheTime = 0;
  private readonly METRICS_CACHE_TTL = 60000; // 1 minute

  private constructor() {
    this.initializePerformanceObserver();
  }

  static getInstance(): CachePerformanceMonitor {
    if (!CachePerformanceMonitor.instance) {
      CachePerformanceMonitor.instance = new CachePerformanceMonitor();
    }
    return CachePerformanceMonitor.instance;
  }

  private initializePerformanceObserver() {
    // Monitor fetch requests using Performance Observer
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation' || entry.entryType === 'resource') {
              const resourceEntry = entry as PerformanceResourceTiming;
              this.recordEntry({
                url: entry.name,
                method: 'GET',
                responseTime: resourceEntry.duration,
                cacheHit: this.isCacheHit(entry),
                timestamp: Date.now(),
                size: resourceEntry.transferSize || 0
              });
            }
          }
        });

        observer.observe({ entryTypes: ['navigation', 'resource'] });
      } catch (error) {
        console.warn('Performance Observer not supported:', error);
      }
    }
  }

  private isCacheHit(entry: globalThis.PerformanceEntry): boolean {
    // Heuristic to determine if request was served from cache
    const resourceEntry = entry as PerformanceResourceTiming;
    
    // If transferSize is 0 and decodedBodySize > 0, likely from cache
    if (resourceEntry.transferSize === 0 && resourceEntry.decodedBodySize > 0) {
      return true;
    }
    
    // Very fast response times might indicate cache hit
    if (resourceEntry.duration < 10) {
      return true;
    }
    
    return false;
  }

  recordEntry(entry: PerformanceEntry) {
    this.entries.push(entry);
    
    // Keep only recent entries
    if (this.entries.length > this.maxEntries) {
      this.entries = this.entries.slice(-this.maxEntries);
    }
    
    // Invalidate metrics cache
    this.metricsCache = null;
  }

  recordFetchRequest(url: string, method: string, startTime: number, cacheHit: boolean, size?: number) {
    const responseTime = Date.now() - startTime;
    this.recordEntry({
      url,
      method,
      responseTime,
      cacheHit,
      timestamp: Date.now(),
      size
    });
  }

  getMetrics(): CacheMetrics {
    const now = Date.now();
    
    // Return cached metrics if still valid
    if (this.metricsCache && (now - this.metricsCacheTime) < this.METRICS_CACHE_TTL) {
      return this.metricsCache;
    }

    // Calculate fresh metrics
    const totalRequests = this.entries.length;
    const cacheHits = this.entries.filter(entry => entry.cacheHit).length;
    const cacheMisses = totalRequests - cacheHits;
    
    const hitRate = totalRequests > 0 ? (cacheHits / totalRequests) * 100 : 0;
    const missRate = totalRequests > 0 ? (cacheMisses / totalRequests) * 100 : 0;
    
    const totalResponseTime = this.entries.reduce((sum, entry) => sum + entry.responseTime, 0);
    const averageResponseTime = totalRequests > 0 ? totalResponseTime / totalRequests : 0;
    
    const totalSize = this.entries.reduce((sum, entry) => sum + (entry.size || 0), 0);
    
    this.metricsCache = {
      hitRate,
      missRate,
      totalRequests,
      averageResponseTime,
      cacheSize: totalSize,
      lastCleanup: 0 // This would be set by cache service
    };
    
    this.metricsCacheTime = now;
    return this.metricsCache;
  }

  getRecentEntries(minutes: number = 5): PerformanceEntry[] {
    const cutoff = Date.now() - (minutes * 60 * 1000);
    return this.entries.filter(entry => entry.timestamp > cutoff);
  }

  getCacheHitsByUrl(): Map<string, { hits: number; misses: number }> {
    const urlStats = new Map<string, { hits: number; misses: number }>();
    
    for (const entry of this.entries) {
      let url: string;
      try {
        url = new URL(entry.url).pathname;
      } catch {
        // If URL is invalid, use the raw URL
        url = entry.url;
      }
      
      const stats = urlStats.get(url) || { hits: 0, misses: 0 };
      
      if (entry.cacheHit) {
        stats.hits++;
      } else {
        stats.misses++;
      }
      
      urlStats.set(url, stats);
    }
    
    return urlStats;
  }

  getSlowRequests(thresholdMs: number = 1000): PerformanceEntry[] {
    return this.entries.filter(entry => entry.responseTime > thresholdMs);
  }

  getCacheEfficiencyReport(): {
    overall: CacheMetrics;
    byUrl: Array<{
      url: string;
      hitRate: number;
      avgResponseTime: number;
      totalRequests: number;
    }>;
    recommendations: string[];
  } {
    const metrics = this.getMetrics();
    const urlStats = this.getCacheHitsByUrl();
    const recommendations: string[] = [];
    
    // Generate URL-specific stats
    const byUrl = Array.from(urlStats.entries()).map(([url, stats]) => {
      const totalRequests = stats.hits + stats.misses;
      const hitRate = totalRequests > 0 ? (stats.hits / totalRequests) * 100 : 0;
      
      const urlEntries = this.entries.filter(entry => {
        try {
          return new URL(entry.url).pathname === url;
        } catch {
          return entry.url === url;
        }
      });
      const avgResponseTime = urlEntries.length > 0 
        ? urlEntries.reduce((sum, entry) => sum + entry.responseTime, 0) / urlEntries.length
        : 0;
      
      return {
        url,
        hitRate,
        avgResponseTime,
        totalRequests
      };
    });
    
    // Generate recommendations
    if (metrics.hitRate < 50) {
      recommendations.push('Cache hit rate is low. Consider increasing cache TTL for static resources.');
    }
    
    if (metrics.averageResponseTime > 500) {
      recommendations.push('Average response time is high. Consider preloading critical resources.');
    }
    
    const slowUrls = byUrl.filter(stat => stat.avgResponseTime > 1000);
    if (slowUrls.length > 0) {
      recommendations.push(`Slow URLs detected: ${slowUrls.map(s => s.url).join(', ')}`);
    }
    
    const lowHitRateUrls = byUrl.filter(stat => stat.hitRate < 30 && stat.totalRequests > 5);
    if (lowHitRateUrls.length > 0) {
      recommendations.push(`URLs with low cache hit rates: ${lowHitRateUrls.map(s => s.url).join(', ')}`);
    }
    
    return {
      overall: metrics,
      byUrl: byUrl.sort((a, b) => b.totalRequests - a.totalRequests),
      recommendations
    };
  }

  clearMetrics() {
    this.entries = [];
    this.metricsCache = null;
    this.metricsCacheTime = 0;
  }

  exportMetrics(): string {
    const report = this.getCacheEfficiencyReport();
    return JSON.stringify(report, null, 2);
  }
}

// Export singleton instance
export const cachePerformanceMonitor = CachePerformanceMonitor.getInstance();

// Utility function to wrap fetch with performance monitoring
export function monitoredFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const startTime = Date.now();
  const method = options.method || 'GET';
  
  return fetch(url, options).then(response => {
    // Determine if this was likely a cache hit
    const cacheHit = response.headers.get('x-cache') === 'HIT' || 
                     response.headers.get('cf-cache-status') === 'HIT' ||
                     (Date.now() - startTime) < 50; // Very fast response
    
    cachePerformanceMonitor.recordFetchRequest(
      url,
      method,
      startTime,
      cacheHit,
      parseInt(response.headers.get('content-length') || '0')
    );
    
    return response;
  }).catch(error => {
    // Record failed request
    cachePerformanceMonitor.recordFetchRequest(
      url,
      method,
      startTime,
      false
    );
    
    throw error;
  });
}